import React, {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  AssetMetaInterface,
  CalendarOptions,
  ComputedPageItem,
  HexColour,
  ProductLayout,
  TaopixDate,
} from '@taopix/taopix-core/dist/types/types';
import {
  ArrowLeftTopIcon,
  Button,
  CalendarIcon,
  DateIcon,
  DatePicker,
  GearIcon,
  Heading,
  HelpTip,
  Horizontal,
  PencilIcon,
  PlusIcon,
  Vertical,
} from '@taopix/taopix-design-system';
import {useTaopix} from '../../Context/TaopixContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import classNames from 'classnames';
import {useSelector} from 'react-redux';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import CellEditorPalette from './CellEditorPalette';
import EventSetsPalette from './EventSetsPalette';
import {CollectionType, CoreEvent, ViewMode} from '@taopix/taopix-core';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import CalendarSettings from './CalendarSettings';
import {CalendarCellStructureSelector, CalendarSelector} from '../../../Selectors';
import {v4 as uuidv4} from 'uuid';

export type EventDataType = {
  [key: string]: CellEditorData;
};
export type CellEditorData = {
  ref: string;
  date: string;
  eventText: string;
  textColour: HexColour | string;
  hiresAssetRef: number | '';
  hasEventSetEvent: boolean;
  dateTitle: string;
};

export type resultType = {
  months: Array<ListItemDataType>;
  days: Array<ListItemDataType>;
  years: Array<ListItemDataType>;
  locales: Array<ListItemDataType>;
};

export type DateDropDown = {
  month: number;
  year: number;
};

export type CalendarPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
  assets: AssetMetaInterface[];
};

const CalendarPalette = React.forwardRef(
  (
    {sectionKey, assets, item}: CalendarPaletteProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const app = useTaopix();
    const ui = useUiState();
    const {experience} = useExperience();
    const {t, i18n} = useTranslation(['calendars']);

    const isSmallScreen = ui.state.isSmallScreen;

    const productLayout = useSelector<any>(state => state.project.productLayout) as ProductLayout;

    const calendarManager = window?.Taopix?.Core?.get('calendarManager');
    const calendarState = useSelector(CalendarSelector);
    const calendarCellStructure = useSelector(CalendarCellStructureSelector);
    const calendarSettings: CalendarOptions = calendarState?.settings;
    const calendarEvents = calendarState?.events;
    const calendarEventSetLinks = calendarState?.eventSetLinks;

    const calendarEventSets = calendarManager.getEventSets();
    const calendarOptions = calendarManager.getOptionMenus();

    const cellHasEventText = Object.keys(calendarCellStructure.items).some(key =>
      key.includes('EVENTTEXT')
    );

    // Calendar experience settings
    const showCalendarEventsPalette =
      experience.EDITOR.calendarEvents.calendarEventsPalette &&
      (calendarSettings.canAddCustomEvents ||
        (calendarSettings.canAddPredefinedEvents &&
          Object.keys(calendarEventSets).length > 0 &&
          cellHasEventText) ||
        calendarSettings.canCustomiseDates);

    const showCalendarSettings =
      experience.EDITOR.calendarSettings.calendarSettingsPalette &&
      (calendarSettings.startYearCanChange ||
        calendarSettings.startMonthCanChange ||
        calendarSettings.startDayCanChange ||
        calendarSettings.localeCanChange);

    const allowCustomEvents =
      calendarSettings.canAddCustomEvents && experience.EDITOR.calendarEvents.allowCustomEvents;

    const canCustomiseDates = calendarSettings.canCustomiseDates;

    const [introTipOpen, setIntoTipOpen] = useState<boolean>(false);
    const [selectedButtonIndex, setSelectedButtonIndex] = useState(
      showCalendarEventsPalette ? 0 : showCalendarSettings ? 1 : 0
    );
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const [showPaletteButtons, setShowPaletteButtons] = useState<boolean>(true);

    const [isLoaded, setIsLoaded] = useState<boolean>(true);

    const [selectedStartDate, setSelectedStartDate] = useState<DateDropDown>({
      month: calendarSettings.startMonth,
      year: calendarSettings.startYear,
    });

    const endDates = Object.keys(calendarState?.monthToPage).reduce(
      (prev, curr) => {
        const timeStamp = new Date(curr).getTime();
        if (timeStamp > Number(prev[1])) {
          return [curr, timeStamp];
        }
        return prev;
      },
      ['', 0]
    );
    const [endYear, endMonth] = String(endDates[0]).split('-');

    const [selectedEndDate, setSelectedEndDate] = useState<DateDropDown>({
      month: Number(endYear),
      year: Number(endMonth),
    });

    // set the custom event to edit either from the custom events list or from calendar cell
    const [customEvent, setCustomEvent] = useState<CellEditorData>({
      ref: '',
      date: '',
      eventText: '',
      textColour: '',
      hiresAssetRef: '',
      hasEventSetEvent: false,
      dateTitle: '',
    });

    const paletteOpen = ui.state.palette.section === 'calendar-palette';

    useEffect(() => {
      if (paletteOpen && !isSmallScreen && (allowCustomEvents || canCustomiseDates)) {
        setIntoTipOpen(true);
      } else {
        setIntoTipOpen(false);
      }
    }, [paletteOpen]);

    const calendarTabsContainerClass = classNames(
      'flex',
      'px-md border-b border-b-themeTextColour20 ',
      'mb-md',
      'lg:space-x-xs',
      'sm:space-x-sm'
    );

    const contentWrapperClasses = classNames('flex', 'flex-1', 'flex-col', 'min-h-0');

    const eventTitleClasses = classNames(
      'break-words',
      'flex-1',
      'line-clamp',
      'line-clamp-1',
      'overflow-ellipsis',
      'overflow-hidden',
      'w-full'
    );

    const eventDateClasses = classNames(
      'break-words',
      'flex-1',
      'line-clamp',
      'line-clamp-1',
      'overflow-ellipsis',
      'overflow-hidden',
      'w-full'
    );

    const calendarEventsListClasses = classNames(
      'flex',
      'flex-col',
      'px-md',
      'overflow-y-auto',
      'overflow-x-hidden',
      // Allow the panel content to be scrolled from behind the toolbar
      experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo' && 'sm:pb-[110rem]'
    );

    const langCode = useMemo(() => {
      return i18n.language.startsWith('zh') ? i18n.language.split('_').join('-') : i18n.language;
    }, [i18n.language]);

    const onSelectSettingsTab = () => {
      setSelectedButtonIndex(1);
    };

    const onSelectEventsTab = () => {
      setSelectedButtonIndex(0);
    };

    // change data into listItemData format for selectLists
    const listItemData = (() => {
      const initialMonths = calendarOptions?.months;
      const initialDays = calendarOptions?.days;
      const locales = calendarOptions?.locales;
      const availableYears = calendarOptions?.years;

      const result: resultType = {
        months: [],
        days: [],
        years: [],
        locales: [],
      };

      // const numberOfMonthsArray = Array.from(
      //   {length: calendarOptions?.numberOfMonths?.max - calendarOptions?.numberOfMonths?.min + 1},
      //   (_, index) => calendarOptions?.numberOfMonths?.min + index
      // );

      // numberOfMonthsArray.forEach(num => {
      //   result.numberOfMonths.push({label: num.toString(), value: num.toString()});
      // });

      for (const key in initialMonths) {
        result.months.push({label: initialMonths[key].month, value: key});
      }

      result.days.push({label: t('*:str_LabelFirstDayOfTheMonth'), value: '0'});
      for (const key in initialDays) {
        result.days.push({label: initialDays[key].day, value: key});
      }

      for (const value in locales) {
        result.locales.push({label: t(`calendars:${locales[value]}`), value: value});
      }

      availableYears?.forEach(y => {
        result.years.push({label: y.toString(), value: y.toString()});
      });

      return result;
    })();

    useEffect(() => {
      setSelectedEndDate({
        month: Number(endMonth),
        year: Number(endYear),
      });
    }, [endYear, endMonth]);

    function convertToTaopixDate(date: Date) {
      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}` as TaopixDate;
    }

    const onCalendarCellClick = (message: string, date: TaopixDate) => {
      if (
        (allowCustomEvents || canCustomiseDates) &&
        showPaletteButtons &&
        experience.EDITOR.calendarEvents.calendarEventsPalette &&
        ui.state.viewMode !== ViewMode.Grid
      ) {
        onSetCustomEvent(date);
      }
    };

    // CALENDAR_CELL_CLICK - for click on calendar product date
    useEffect(() => {
      const token = app.core.subscribe(CoreEvent.CALENDAR_CELL_CLICK, onCalendarCellClick);
      return () => {
        app.core.unsubscribe(token);
      };
    }, [ui.state.viewMode]);

    // hide events palette and only show settings if the user cannot do anything with events
    // only when all options are false will palette hide completely
    useEffect(() => {
      setShowPaletteButtons(
        Object.keys(calendarEventSetLinks)?.length > 0 ||
          showCalendarEventsPalette ||
          showCalendarSettings
      );
    }, [calendarSettings, calendarEventSetLinks]);

    useEffect(() => {
      if (!showPaletteButtons) {
        if (listItemData) {
          onSelectSettingsTab();
        }
      }
    }, [showPaletteButtons, listItemData]);

    const onApplySettingsChange = (data: Partial<CalendarOptions>) => {
      ui.dispatch(UserInterfaceActions.setIsBusy(true));
      calendarManager
        .updateSettings(data)
        .then(() => {
          // set the selected start dates here in state as they are needed for the datePicker
          if (data.startMonth && data.startYear) {
            setSelectedStartDate({
              month: data.startMonth,
              year: data.startYear,
            });
          }
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        })
        .catch(() => {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        });
    };

    function getDaySuffix(day) {
      if (day >= 11 && day <= 13) {
        return 'th';
      }
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    }

    // Will return the date title for the CellEditorPalette (full) or the sidebar event set option.
    const getDateTitle = (date: string, full?: boolean): string => {
      const [numYear, numMonth, numDay] = date.split('-').map(date => Number(date));
      // get Date in UTC to keep it consitent
      const currentDate = new Date(Date.UTC(numYear, numMonth - 1, numDay));

      // get formatOptions based on if we want 'full' date for the palette title or 'short' for sidebar event option.
      const dateTimeFormatOptions: Intl.DateTimeFormatOptions = full
        ? {month: 'long', timeZone: 'UTC'}
        : {
            month: 'short',
            timeZone: 'UTC',
            year: 'numeric',
            day: 'numeric',
            weekday: 'short',
          };

      const formatter = new Intl.DateTimeFormat(langCode, dateTimeFormatOptions);

      if (!full) {
        const formatted = formatter.format(currentDate);
        return formatted;
      }

      const month = formatter.format(currentDate);
      const year = currentDate.getUTCFullYear();
      const dayWithSuffix = numDay + getDaySuffix(numDay);

      return `${dayWithSuffix} ${month} ${year}`;
    };

    const onSetCustomEvent = async (date: any) => {
      setIsLoaded(false);
      ui.dispatch(UserInterfaceActions.setToolPaletteSection(`${sectionKey}/cell-editor-palette`));
      calendarManager
        .initCellPreview('cell-preview', date)
        .then(response => {
          setCustomEvent({
            ref: `${date}-${uuidv4()}`,
            date: date,
            eventText: response.eventText,
            textColour: response.textColour,
            hiresAssetRef: response.hiresAssetRef,
            hasEventSetEvent: response.hasEventSetEvent,
            dateTitle: getDateTitle(date, true),
          });
          setIsLoaded(true);
        })
        .catch(error => {
          ui.dispatch(UserInterfaceActions.setToolPaletteSection(`${sectionKey}`));
          console.error(error);
          setIsLoaded(true);
          ui.dispatch(
            UserInterfaceActions.setToolPalette({
              confirmationRequired: true,
            })
          );
        });
    };

    // if the product type is calendar and the setting for either setttings/events is on return the palette
    if (
      productLayout.type === CollectionType.Calendar &&
      (showCalendarEventsPalette || showCalendarSettings)
    ) {
      return (
        <>
          <ToolPaletteSection
            id={'calendar-palette'}
            sectionKey={sectionKey}
            title={t('calendars:str_TitleCalendar')}
            buttonIcon={<CalendarIcon />}
            ref={ref}
            showSeparator={false}
            smallScreenPanelHeight={'full'}
          >
            <div className={contentWrapperClasses} id={'calendar-palette-section'}>
              {showPaletteButtons && showCalendarEventsPalette && showCalendarSettings && (
                <div id="calendar-tabs-container" className={calendarTabsContainerClass}>
                  <Button
                    label={t('*:str_LabelEvents')}
                    className={'flex-1' + (selectedButtonIndex === 0 ? ' -mb-[1px]' : '')}
                    buttonStyle={'tab'}
                    tabPosition={'top'}
                    size={'small'}
                    onClick={onSelectEventsTab}
                    aria-pressed={selectedButtonIndex === 0}
                    startIcon={<DateIcon />}
                  />
                  <Button
                    label={t('*:str_LabelSettings')}
                    labelLines={1}
                    className={'flex-1' + (selectedButtonIndex === 1 ? ' -mb-[1px]' : '')}
                    buttonStyle={'tab'}
                    size={'small'}
                    tabPosition={'top'}
                    onClick={onSelectSettingsTab}
                    aria-pressed={selectedButtonIndex === 1}
                    startIcon={<GearIcon />}
                  />
                </div>
              )}
              {selectedButtonIndex === 0 && showCalendarEventsPalette && (
                <div className={calendarEventsListClasses} id={'calendar-events-section'}>
                  {/* (calendarSettings.canAddCustomEvents || calendarSettings.canCustomiseDates) */}
                  {allowCustomEvents || canCustomiseDates ? (
                    <div className={'flex flex-col space-y-sm  mb-lg'}>
                      <>
                        <Button
                          id={'selectDateButton'}
                          size={ui.state.isSmallScreen ? 'medium' : 'small'}
                          buttonStyle={'secondary'}
                          label={t('calendars:str_LabelAddAnEvent')}
                          onClick={() => setDatePickerOpen(!datePickerOpen)}
                          startIcon={<PlusIcon />}
                        />
                        <DatePicker
                          id={'calendar-datepicker'}
                          open={datePickerOpen}
                          updateSelectedDate={(date: Date, event: SyntheticEvent) => {
                            const convertedDate = convertToTaopixDate(date);
                            onSetCustomEvent(convertedDate);
                          }}
                          anchorId={'selectDateButton'}
                          onClose={() => {
                            setDatePickerOpen(false);
                          }}
                          displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
                          minDate={new Date(selectedStartDate.year, selectedStartDate.month - 1, 1)} // minimum date of calendar
                          fromDate={
                            new Date(selectedStartDate.year, selectedStartDate.month - 1, 1)
                          } // the selected day when popout is initally opened
                          maxDate={new Date(selectedEndDate.year, selectedEndDate.month, 0)} // maximum data of calendar
                          nextAriaLabel={t('calendars:str_LabelNextMonth', {
                            defaultValue: 'Next month',
                          })}
                          prevAriaLabel={t('calendars:str_LabelPreviousMonth', {
                            defaultValue: 'Previous month',
                          })}
                        />
                      </>
                      {calendarEvents &&
                        Object.entries(calendarEvents).map(([date, eventData]: any) => {
                          // check the date to ensure it is within the calendar start and end dates, if not disable the button.
                          const isAvailableDate = calendarManager?.checkAvailableDate(date);
                          const buttonClick = () => {
                            const [year, month, day] = date.split('-');
                            onSetCustomEvent(`${year}-${month}-${day}`);
                          };

                          const [numYear, numMonth, numDay] = date
                            .split('-')
                            .map(date => Number(date));
                          const currentDate = new Date(Date.UTC(numYear, numMonth - 1, numDay));

                          const formattedDateTitle = getDateTitle(date);
                          return (
                            <Button
                              key={`custom-event-${date}`}
                              label={t('calendars:str_LabelEditEvent', {
                                defaultValue: 'Edit event',
                              })}
                              hideLabel
                              buttonStyle={'tertiary'}
                              labelAlignment="left"
                              className={'p-sm'}
                              justified
                              endIcon={isAvailableDate ? <PencilIcon /> : null}
                              disabled={!isAvailableDate}
                              onClick={isAvailableDate ? buttonClick : () => {}}
                            >
                              <span className={'flex flex-col text-left max-w-[85%]'}>
                                <h1 className={eventTitleClasses}>{eventData.eventText}</h1>
                                <time
                                  dateTime={currentDate.toISOString()}
                                  className={classNames(
                                    eventDateClasses,
                                    eventData.eventText && 'opacity-60'
                                  )}
                                >
                                  {formattedDateTitle}
                                </time>
                              </span>
                            </Button>
                          );
                        })}
                    </div>
                  ) : (
                    <></>
                  )}
                  {Object.keys(calendarEventSets).length > 0 && cellHasEventText && (
                    <div className={'flex flex-col space-y-sm'}>
                      <Heading level={1} size={4}>
                        {t('calendars:str_LabelOtherEvents')}
                      </Heading>
                      <EventSetsPalette
                        sectionKey={'event-sets'}
                        calendarSettings={calendarSettings}
                      />
                    </div>
                  )}
                </div>
              )}
              {selectedButtonIndex === 1 && showCalendarSettings && (
                <div className={'flex-1 flex px-md h-full min-h-0'}>
                  <CalendarSettings
                    listItemData={listItemData}
                    onApplySettingsChange={onApplySettingsChange}
                    calendarOptions={calendarOptions}
                    calendarSettings={calendarSettings}
                  />
                </div>
              )}
            </div>
            <CellEditorPalette
              sectionKey={'cell-editor-palette'}
              assets={assets}
              cellEvent={customEvent}
              item={item}
              loaded={isLoaded}
            />
            <HelpTip
              id={'calendar-palette-intro-tip'}
              open={introTipOpen}
              message={t('*:str_ToolTipAddCalendarEvent')}
              buttonLabel={t('*:str_ButtonOK')}
              buttonFunction={() => setIntoTipOpen(false)}
              anchorId={'calendar-events-section'}
              anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
              transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
              offset={{x: 10, y: 0}}
              icon={<ArrowLeftTopIcon />}
              className={'max-w-[240rem]'}
              showAfter={1000}
              hideAfter={10000}
              showMode={'once'}
            />
          </ToolPaletteSection>
        </>
      );
    } else {
      return null;
    }
  }
);

export default CalendarPalette;
